import type {
  ArenaConfig,
  BonusConfig,
  BonusId,
  BonusPopulationConfig,
} from "../config/gameplay-config";
import type { Vector2 } from "./player-movement";
import type { RandomSource } from "./seeded-random";
import type { SpawnExclusionZone } from "./spawn-exclusions";

export interface BonusEntity {
  readonly id: string;
  readonly position: Vector2;
  readonly radius: number;
  readonly type: BonusId;
}

export interface BonusPopulationOptions {
  readonly arena: ArenaConfig;
  readonly bonuses: readonly BonusConfig[];
  readonly config: BonusPopulationConfig;
  readonly initialPlayerPosition: Vector2;
  readonly random: RandomSource;
}

const squaredDistance = (left: Vector2, right: Vector2): number => {
  const x = left.x - right.x;
  const y = left.y - right.y;
  return x * x + y * y;
};

export class BonusPopulation {
  private readonly bonuses: BonusEntity[] = [];
  private nextIdentifier = 1;

  public constructor(private readonly options: BonusPopulationOptions) {
    this.fill();
  }

  public getSnapshot(): readonly BonusEntity[] {
    return this.bonuses.map((bonus) => ({
      ...bonus,
      position: { ...bonus.position },
    }));
  }

  public remove(
    identifier: string,
    exclusions: readonly SpawnExclusionZone[] = [],
  ): BonusEntity | null {
    const removed = this.bonuses.find((bonus) => bonus.id === identifier);
    if (removed === undefined) {
      return null;
    }
    const index = this.bonuses.indexOf(removed);
    this.bonuses.splice(index, 1);
    if (this.bonuses.length < this.options.config.target) {
      this.spawn(removed.type, [], exclusions);
    }
    return { ...removed, position: { ...removed.position } };
  }

  public spawnNear(type: BonusId, position: Vector2): BonusEntity {
    const config = this.requireConfig(type);
    const entity = this.createEntity(type, config.radius, position);
    this.bonuses.push(entity);
    return { ...entity, position: { ...entity.position } };
  }

  private createEntity(
    type: BonusId,
    radius: number,
    position: Vector2,
  ): BonusEntity {
    const entity = {
      id: `bonus-${String(this.nextIdentifier).padStart(4, "0")}`,
      position: { ...position },
      radius,
      type,
    };
    this.nextIdentifier += 1;
    return entity;
  }

  private fill(): void {
    for (const bonus of this.options.bonuses) {
      while (this.bonuses.length < this.options.config.target) {
        if (!this.spawn(bonus.id, [this.options.initialPlayerPosition])) {
          throw new Error("Bonus population could not reach its target");
        }
      }
    }
  }

  private isValid(
    position: Vector2,
    radius: number,
    exclusions: readonly Vector2[],
    entityExclusions: readonly SpawnExclusionZone[],
  ): boolean {
    const inset =
      this.options.arena.boundaryInset +
      this.options.config.spawnInset +
      radius;
    if (
      position.x < inset ||
      position.x > this.options.arena.width - inset ||
      position.y < inset ||
      position.y > this.options.arena.height - inset
    ) {
      return false;
    }
    if (
      entityExclusions.some(
        (exclusion) =>
          squaredDistance(position, exclusion.position) <
          (radius + exclusion.radius) ** 2,
      )
    ) {
      return false;
    }
    if (
      exclusions.some(
        (excluded) =>
          squaredDistance(position, excluded) <
          this.options.config.initialPlayerExclusionRadius ** 2,
      )
    ) {
      return false;
    }
    return this.bonuses.every(
      (bonus) =>
        squaredDistance(position, bonus.position) >=
        (radius + bonus.radius + this.options.config.minimumSpacing) ** 2,
    );
  }

  private requireConfig(type: BonusId): BonusConfig {
    const config = new Map(
      this.options.bonuses.map((bonus) => [bonus.id, bonus] as const),
    ).get(type);
    if (config === undefined) {
      throw new Error(`Unknown bonus type: ${type}`);
    }
    return config;
  }

  private spawn(
    type: BonusId,
    exclusions: readonly Vector2[],
    entityExclusions: readonly SpawnExclusionZone[] = [],
  ): boolean {
    const bonus = this.requireConfig(type);
    const inset =
      this.options.arena.boundaryInset +
      this.options.config.spawnInset +
      bonus.radius;
    for (
      let attempt = 0;
      attempt < this.options.config.maximumSpawnAttemptsPerBonus;
      attempt += 1
    ) {
      const position = {
        x:
          inset +
          this.options.random.nextUnit() *
            (this.options.arena.width - inset * 2),
        y:
          inset +
          this.options.random.nextUnit() *
            (this.options.arena.height - inset * 2),
      };
      if (!this.isValid(position, bonus.radius, exclusions, entityExclusions)) {
        continue;
      }
      this.bonuses.push(this.createEntity(type, bonus.radius, position));
      return true;
    }
    return false;
  }
}
